import { Table, Button, Input, Switch, Alert } from "antd";
import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DownloadOutlined } from "@ant-design/icons";
import { tablePreparation } from "../../../services/dataHandling";
import RevenueChart from "../../charts/revenueChart";
import TableCellWithEvolution from "../../layout/tableCellWithEvolution";
import { getComparisonData } from "../../../stateManagement/actions/report";
import { StringUtils } from "../../../utils/strings";
import { exportToXLSX } from "../../../services/exportReport";
import { PARTITION_THD, PARTITION_MAIN } from "../../../utils/constants";
import {
    asNumber,
    advertiserLabel,
    prepareAdvertiserRows,
    prepareComparisonRows,
    filterRows,
    compareTableRows,
    childNumberMap,
} from "../../../services/dealsTableData.js";

const EMPTY_DATA = [];
const EMPTY_QUERY = {};
const DealsSummaryTable = ({
    fullData = EMPTY_DATA,
    dimensionOverride,
    detailQuery = EMPTY_QUERY,
    allowAdvertiserGrouping = true,
    enableNavigation = true,
    ready = true,
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const report = useSelector((state) => state.reportReducer);
    const { comparison, period, filterBy, publisherView } = report;
    const dimension =
        dimensionOverride === undefined ? report.dimension : dimensionOverride;
    const partition = location.pathname.startsWith("/thd")
        ? PARTITION_THD
        : PARTITION_MAIN;
    const [comparisonData, setComparisonData] = useState([]);
    const [comparisonLoading, setComparisonLoading] = useState(false);
    const [comparisonError, setComparisonError] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [searchText, setSearchText] = useState("");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const [groupByAdvertiser, setGroupByAdvertiser] = useState(true);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [sorting, setSorting] = useState({
        field: "revenue",
        order: "descend",
    });
    const rows = Array.isArray(fullData) ? fullData : EMPTY_DATA;
    const detailKey = JSON.stringify(detailQuery);
    const comparisonKey = JSON.stringify({
        period,
        dimension,
        filterBy,
        publisherView,
        partition,
        detailKey,
    });
    const [loadedComparisonKey, setLoadedComparisonKey] = useState(null);
    const activeComparisonData =
        loadedComparisonKey === comparisonKey ? comparisonData : EMPTY_DATA;
    const source = comparison ? activeComparisonData : rows;
    const hasAdvertiserData =
        allowAdvertiserGrouping &&
        source.some((row) =>
            Object.prototype.hasOwnProperty.call(row, "orderAdvertiser"),
        );
    const grouped = hasAdvertiserData && groupByAdvertiser;
    const simpleData = useMemo(
        () =>
            tablePreparation(rows, dimension).map((row) => ({
                ...row,
                rawDimension: row.dimension,
                ...(dimension === "orderAdvertiser"
                    ? { orderAdvertiser: row.dimension, isAdvertiser: true }
                    : {}),
            })),
        [rows, dimension],
    );
    const advertiserData = useMemo(
        () => prepareAdvertiserRows(rows, dimension),
        [rows, dimension],
    );
    const comparisonRows = useMemo(
        () => prepareComparisonRows(activeComparisonData, dimension, grouped),
        [activeComparisonData, dimension, grouped],
    );

    useEffect(() => {
        let active = true;
        setComparisonError("");
        if (!comparison || !ready) {
            setComparisonLoading(false);
            return () => {
                active = false;
            };
        }
        setComparisonLoading(true);
        dispatch(
            getComparisonData(
                {
                    period,
                    dimension,
                    filterBy,
                    publisherView,
                    partition,
                    isDealsPage: true,
                    ...JSON.parse(detailKey),
                },
                (result) => {
                    if (!active) return;
                    setComparisonData(Array.isArray(result) ? result : []);
                    setLoadedComparisonKey(comparisonKey);
                    setComparisonLoading(false);
                },
                () => {
                    if (!active) return;
                    setComparisonData([]);
                    setLoadedComparisonKey(comparisonKey);
                    setComparisonLoading(false);
                    setComparisonError("Could not load comparison data.");
                },
            ),
        );
        return () => {
            active = false;
        };
    }, [
        dispatch,
        comparison,
        ready,
        period,
        dimension,
        filterBy,
        publisherView,
        partition,
        detailKey,
        comparisonKey,
    ]);

    useEffect(() => {
        const timer = setTimeout(() => setSearchText(searchInput), 500);
        return () => clearTimeout(timer);
    }, [searchInput]);
    useEffect(() => {
        setPagination((previous) => ({ ...previous, current: 1 }));
        setExpandedKeys([]);
    }, [searchText, grouped, comparisonKey, comparison, fullData]);

    const tableData = comparison
        ? comparisonRows
        : grouped
          ? advertiserData
          : simpleData;
    const filteredData = useMemo(
        () => filterRows(tableData, searchText, grouped),
        [tableData, searchText, grouped],
    );
    const compareRows = (a, b, field) =>
        compareTableRows(a, b, field, comparison);
    const childNumbers = grouped
        ? childNumberMap(filteredData, sorting, comparison)
        : new Map();
    const destination = (record) => {
        if (!enableNavigation) return null;
        const base = partition === PARTITION_THD ? "/thd/deals" : "/deals";
        if (record.isAdvertiser || dimension === "orderAdvertiser") {
            const advertiser = record.orderAdvertiser ?? record.rawDimension;
            if (!advertiser)
                return `${base}/Unknown%20advertiser?scope=advertiser&missing=1`;
            return `${base}/${encodeURIComponent(advertiser)}?scope=advertiser`;
        }
        if (dimension !== "dealName" || !record.rawDimension) return null;
        const query = new URLSearchParams();
        if (Object.prototype.hasOwnProperty.call(record, "orderAdvertiser"))
            query.set("advertiser", record.orderAdvertiser);
        return `${base}/${encodeURIComponent(record.rawDimension)}${query.toString() ? `?${query.toString()}` : ""}`;
    };
    const sortable = (field) => ({
        key: field,
        sorter: (a, b) => compareRows(a, b, field),
        sortOrder: sorting.field === field ? sorting.order : null,
    });
    const columns = [
        {
            title: "#",
            key: "index",
            width: 60,
            render: (_, record, index) =>
                grouped
                    ? record.isAdvertiser
                        ? ""
                        : childNumbers.get(record.key)
                    : (pagination.current - 1) * pagination.pageSize +
                      index +
                      1,
        },
        {
            title: grouped
                ? "Advertiser / Dimension"
                : dimension === "website"
                  ? "Website"
                  : "Dimension",
            dataIndex: "dimension",
            ...sortable("dimension"),
            render: (value, record) => {
                const href = destination(record);
                const style = {
                    color: "#808080",
                    ...(record.isAdvertiser ? { fontWeight: 700 } : {}),
                };
                return href ? (
                    <Link to={href} style={style}>
                        {value}
                    </Link>
                ) : (
                    <span style={style}>{value}</span>
                );
            },
        },
        ...["revenue", "impressions", "cpm", "ctr"].map((metric) => ({
            title: {
                revenue: "Revenue (€)",
                impressions: "Impressions",
                cpm: "CPM (€)",
                ctr: "CTR (%)",
            }[metric],
            dataIndex: metric,
            align: "right",
            width: comparison ? 180 : 140,
            ...sortable(metric),
            render: (value) =>
                comparison ? (
                    <TableCellWithEvolution
                        cellValue={value?.[0]}
                        evolutionValue={value?.[1]}
                        refValue={value?.[2]}
                        fillWithZeros={metric === "revenue"}
                    />
                ) : ["revenue", "impressions"].includes(metric) ? (
                    StringUtils.getUSnumbersFormating(
                        value,
                        metric === "revenue",
                    )
                ) : (
                    asNumber(value).toFixed(2)
                ),
        })),
    ];
    if (!comparison)
        columns.splice(
            3,
            0,
            {
                title: "Rev.share (%)",
                dataIndex: "revenueShare",
                width: 140,
                ...sortable("revenueShare"),
                render: (value) => (parseFloat(value) || 0).toFixed(2),
            },
            {
                title: "Rev.chart",
                dataIndex: "revenueChart",
                key: "revenueChart",
                width: 160,
                render: (value) => (
                    <RevenueChart chartData={value} period={period} />
                ),
            },
        );
    const handleExport = () => {
        // Keep the original export contract: current-period values, including while comparing.
        if (!grouped) return exportToXLSX(simpleData);
        const exportRows = advertiserData
            .flatMap((parent) => parent.children || [parent])
            .map(({ children, isAdvertiser, ...row }) => ({
                ...row,
                dimension: isAdvertiser
                    ? row.dimension
                    : `${advertiserLabel(row.orderAdvertiser)} / ${row.dimension}`,
                orderAdvertiser: advertiserLabel(row.orderAdvertiser),
            }));
        return exportToXLSX(exportRows);
    };
    return (
        <div className="table-container">
            <div
                className="spaced-between-container"
                style={{ width: "100%", gap: 16, flexWrap: "wrap" }}
            >
                <Input.Search
                    placeholder={
                        grouped
                            ? "Search advertiser or campaign"
                            : "Search in table"
                    }
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    allowClear
                    style={{ marginBottom: 16, width: 300 }}
                />
                {hasAdvertiserData && (
                    <label style={{ marginBottom: 16 }}>
                        <Switch
                            checked={groupByAdvertiser}
                            onChange={setGroupByAdvertiser}
                        />
                        <span style={{ marginLeft: 8 }}>
                            Group by advertiser
                        </span>
                    </label>
                )}
                <Button
                    className="std-button"
                    size="large"
                    onClick={handleExport}
                    disabled={!ready}
                >
                    <DownloadOutlined /> Export
                </Button>
            </div>
            {comparisonError && (
                <Alert
                    type="error"
                    message={comparisonError}
                    showIcon
                    style={{ marginBottom: 12 }}
                />
            )}
            {grouped && (
                <div style={{ marginBottom: 12, color: "#666" }}>
                    Search filters campaigns; advertiser totals remain
                    unchanged.
                    {!comparison &&
                        " Revenue share is calculated against the full report."}
                </div>
            )}
            <Table
                rowKey="key"
                key={comparison ? "comparison" : "simple"}
                className="summary-table"
                columns={columns}
                dataSource={ready ? filteredData : []}
                loading={comparisonLoading || !ready}
                scroll={{ x: "70vw" }}
                expandable={
                    grouped
                        ? {
                              expandedRowKeys: searchText.trim()
                                  ? filteredData
                                        .filter((row) => row.children?.length)
                                        .map((row) => row.key)
                                  : expandedKeys,
                              onExpandedRowsChange: setExpandedKeys,
                              indentSize: 24,
                          }
                        : undefined
                }
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) =>
                        `Total: ${total}${grouped ? " advertisers" : ""}`,
                }}
                onChange={(nextPagination, filters, sorter, extra) => {
                    setPagination({
                        current:
                            extra.action === "sort"
                                ? 1
                                : nextPagination.current,
                        pageSize: nextPagination.pageSize,
                    });
                    if (!Array.isArray(sorter))
                        setSorting({
                            field: sorter.columnKey || "revenue",
                            order: sorter.order || null,
                        });
                }}
            />
        </div>
    );
};
export default DealsSummaryTable;